import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import { useMutation } from '@tanstack/react-query';
import { BadgeAlt as Badge, Button, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Checkbox, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Skeleton, useToast, } from '@cargomatic/ui';
import { AlertCircle, ChevronDown, ChevronRight, CheckCircle, X, Info, ExternalLink, MoreHorizontal, Edit, } from 'lucide-react';
import { cn } from 'utils/ui/classes';
import { useOrderRTBArLineItems } from './hooks/useOrderRTBLineItems';
import ShipmentExternalLink from './ShipmentExternalLink';
import ShipperRateCardLink from './ShipperRateCardLink';
import InvoiceConfiguration from './InvoiceConfiguration';
import { formatDate } from 'utils/formatters/Date';
import { removeLineItem } from 'utils/AccountingWebAPIUtils';
import CargomaticModal from 'components/v2/modal';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { canShowEditAction, canShowDeleteAction, isValidStatus, } from 'helpers/accounting';
import SessionStore from 'stores/SessionStore';
import { NO_DATA } from './constants';
import InvoiceStatusBadge from 'components/v2/InvoiceStatusBadge';
import MissingEquipmentWarning from './MissingEquipmentWarning';
import { rerateOrderCosts, deleteOrderLineItem } from 'utils/OrderWebAPIUtils';
import { RerateCostsDialog } from './RerateCostsDialog';
import GenerateInvoiceDialog from './GenerateInvoiceDialog';
import { isReadyToBill, } from './hooks/useOrderBillingStatus';
const isOrderLevelBaseCostOrFsc = (charge) => {
    var _a;
    return !((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a.shipment_id) &&
        (charge.category === 'base_cost' || charge.type === 'fuel_surcharge');
};
export function ARChargesTable({ orderId, shipperId, billingStatus, onEditCharge, groupLikeInvoiceItems, onAddBaseCost, showAddBaseCost, }) {
    var _a;
    const { t } = useTranslation();
    const { toast } = useToast();
    const isFinance = SessionStore.isFinance() || SessionStore.isFinanceAdmin();
    // Fetch AR line items
    const { data: arLines = [], isLoading: isLoadingAR, isError: isErrorAR, refetch, } = useOrderRTBArLineItems(orderId);
    useEffect(() => {
        if (isErrorAR) {
            toast({
                title: 'Error',
                description: 'Failed to load line items.',
                variant: 'destructive',
            });
        }
    }, [isErrorAR, toast]);
    // Internal state
    const [arFilter, setArFilter] = useState('all');
    const [showOptionalColumns, setShowOptionalColumns] = useState(false);
    const [selectedCharges, setSelectedCharges] = useState(new Set());
    const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
    const [selectedInvoiceCharge, setSelectedInvoiceCharge] = useState(null);
    const [showConfirmVoidModal, setShowConfirmVoidModal] = useState(false);
    const [chargeToVoid, setChargeToVoid] = useState(null);
    const [voidPending, setVoidPending] = useState(false);
    // Invoice generation state
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    // Derived data
    const filteredCharges = useMemo(() => {
        if (arFilter === 'all')
            return arLines;
        if (arFilter === 'actioned') {
            return arLines.filter((c) => { var _a; return (_a = c.invoice_id) === null || _a === void 0 ? void 0 : _a.number; });
        }
        return arLines.filter((c) => { var _a; return !((_a = c.invoice_id) === null || _a === void 0 ? void 0 : _a.number); });
    }, [arLines, arFilter]);
    const filteredTotal = useMemo(() => {
        return filteredCharges.reduce((sum, c) => sum + (c.value || 0), 0);
    }, [filteredCharges]);
    const unactionedCharges = useMemo(() => {
        return arLines.filter((c) => { var _a; return !((_a = c.invoice_id) === null || _a === void 0 ? void 0 : _a.number); });
    }, [arLines]);
    const allChargesActioned = unactionedCharges.length === 0;
    const allUnactionedSelected = useMemo(() => {
        if (unactionedCharges.length === 0)
            return false;
        return unactionedCharges.every((c) => selectedCharges.has(c._id));
    }, [unactionedCharges, selectedCharges]);
    // Handlers
    const handleSelectAll = useCallback((checked) => {
        if (checked) {
            const newSet = new Set(selectedCharges);
            unactionedCharges.forEach((c) => newSet.add(c._id));
            setSelectedCharges(newSet);
        }
        else {
            const newSet = new Set(selectedCharges);
            unactionedCharges.forEach((c) => newSet.delete(c._id));
            setSelectedCharges(newSet);
        }
    }, [unactionedCharges, selectedCharges]);
    const handleSelectCharge = useCallback((chargeId, checked) => {
        const newSet = new Set(selectedCharges);
        if (checked) {
            newSet.add(chargeId);
        }
        else {
            newSet.delete(chargeId);
        }
        setSelectedCharges(newSet);
    }, [selectedCharges]);
    const handleInitiateVoid = useCallback((charge) => {
        setChargeToVoid(charge);
        setShowConfirmVoidModal(true);
    }, []);
    const voidOrderLineItem = useCallback(async (charge) => {
        var _a;
        const lineItemId = charge._id;
        const orderId = (_a = charge.order) === null || _a === void 0 ? void 0 : _a._id;
        if (!lineItemId || !orderId) {
            toast({
                title: 'Error',
                description: 'Cannot void charge: missing line item or order ID.',
                variant: 'destructive',
            });
            return;
        }
        await deleteOrderLineItem(lineItemId, orderId);
    }, [toast]);
    const voidShipmentLineItem = useCallback(async (charge) => {
        var _a;
        const adjustmentId = charge.adjustment_id;
        const shipmentId = (_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id;
        if (!adjustmentId || !shipmentId) {
            toast({
                title: 'Error',
                description: 'Cannot void charge: missing adjustment or shipment ID.',
                variant: 'destructive',
            });
            return;
        }
        await removeLineItem(adjustmentId, 'receivables', shipmentId);
    }, [toast]);
    const handleConfirmVoid = useCallback(async () => {
        var _a, _b;
        if (!chargeToVoid)
            return;
        setVoidPending(true);
        try {
            if ((_a = chargeToVoid.order) === null || _a === void 0 ? void 0 : _a._id) {
                await voidOrderLineItem(chargeToVoid);
            }
            else if ((_b = chargeToVoid.shipment) === null || _b === void 0 ? void 0 : _b._id) {
                await voidShipmentLineItem(chargeToVoid);
            }
            else {
                toast({
                    title: 'Error',
                    description: 'Failed to void the charge. No order or shipment associated with this charge.',
                    variant: 'destructive',
                });
                return;
            }
            toast({
                title: 'Charge Voided',
                description: 'The charge has been successfully voided.',
            });
            refetch();
        }
        catch (_c) {
            toast({
                title: 'Error',
                description: 'Failed to void the charge. Please try again.',
                variant: 'destructive',
            });
        }
        finally {
            setVoidPending(false);
            setShowConfirmVoidModal(false);
            setChargeToVoid(null);
        }
    }, [chargeToVoid, toast, refetch, voidOrderLineItem, voidShipmentLineItem]);
    const handleInvoiceSuccess = useCallback(() => {
        setSelectedCharges(new Set());
        setInvoiceModalOpen(false);
        refetch();
    }, [refetch]);
    const handleViewInvoice = useCallback((charge) => {
        setSelectedInvoiceCharge(charge);
        setShowInvoiceDetailsModal(true);
    }, []);
    const selectedChargeObjects = useMemo(() => {
        return arLines.filter((c) => selectedCharges.has(c._id));
    }, [arLines, selectedCharges]);
    const hasSelectedWithoutEquipment = useMemo(() => {
        return selectedChargeObjects.some((c) => { var _a, _b, _c; return !((_a = c.equipment) === null || _a === void 0 ? void 0 : _a.equipment_id) && !((_c = (_b = c.invoice_id) === null || _b === void 0 ? void 0 : _b.number) === null || _c === void 0 ? void 0 : _c.startsWith('CM-')); });
    }, [selectedChargeObjects]);
    const createInvoiceDisabled = selectedCharges.size === 0 ||
        allChargesActioned ||
        hasSelectedWithoutEquipment ||
        !isReadyToBill(billingStatus);
    const createInvoiceDisabledReason = !isReadyToBill(billingStatus)
        ? 'Order billing status is not ready to bill.'
        : allChargesActioned
            ? 'All charge lines are already invoiced.'
            : hasSelectedWithoutEquipment
                ? 'Equipment assignment required to invoice'
                : selectedCharges.size === 0
                    ? 'No charges selected'
                    : undefined;
    // Rerate state & logic
    const [showRerateDialog, setShowRerateDialog] = useState(false);
    const AR_RERATE_ELIGIBLE_TYPES = ['shipper_cost', 'fuel_surcharge'];
    const isRerateEnabled = useMemo(() => {
        if (selectedChargeObjects.length === 0)
            return false;
        return selectedChargeObjects.every((c) => { var _a; return AR_RERATE_ELIGIBLE_TYPES.includes((_a = c.type) !== null && _a !== void 0 ? _a : ''); });
    }, [selectedChargeObjects]);
    const rerateEligibleCharges = useMemo(() => {
        return selectedChargeObjects.filter((c) => { var _a; return AR_RERATE_ELIGIBLE_TYPES.includes((_a = c.type) !== null && _a !== void 0 ? _a : ''); });
    }, [selectedChargeObjects]);
    const rerateMutation = useMutation({
        mutationFn: (chargeIds) => {
            const selectedItems = rerateEligibleCharges.filter((c) => chargeIds.includes(c._id));
            const resolvedIds = selectedItems.map((c) => c.category === 'base_cost' ? c._id : c.adjustment_id || c._id);
            return rerateOrderCosts(orderId, 'shipper', resolvedIds);
        },
        onSuccess: () => {
            toast({
                title: 'Rerate Successful',
                description: 'Costs have been rerated successfully.',
            });
            setSelectedCharges(new Set());
            setShowRerateDialog(false);
            refetch();
        },
        onError: (err) => {
            var _a, _b;
            const message = (_b = (_a = err === null || err === void 0 ? void 0 : err.entity) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'An error occurred.';
            toast({
                title: 'Rerate Failed',
                description: message,
                variant: 'destructive',
            });
        },
    });
    const handleRerate = useCallback((chargeIds) => {
        if (!orderId) {
            toast({
                title: 'Rerate Failed',
                description: 'Unable to rerate: missing order ID.',
                variant: 'destructive',
            });
            return;
        }
        rerateMutation.mutate(chargeIds);
    }, [orderId, rerateMutation, toast]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "grid gap-3 sm:flex sm:flex-wrap sm:items-center sm:justify-between mb-4" },
            React.createElement("div", { className: "flex items-center gap-3 flex-wrap" },
                React.createElement("h2", { className: "text-lg font-semibold" }, "Accounts Receivable"),
                React.createElement(Select, { value: arFilter, onValueChange: (value) => setArFilter(value) },
                    React.createElement(SelectTrigger, { className: "w-full sm:w-[220px]", onClick: (e) => e.stopPropagation() },
                        React.createElement(SelectValue, null)),
                    React.createElement(SelectContent, null,
                        React.createElement(SelectItem, { value: "all" }, "All Revenue"),
                        React.createElement(SelectItem, { value: "actioned" }, "Actioned Revenue"),
                        React.createElement(SelectItem, { value: "unactioned" }, "Unactioned Pending Revenue")))),
            React.createElement("div", { className: "flex flex-col items-end gap-1" },
                React.createElement(DropdownMenu, null,
                    React.createElement(DropdownMenuTrigger, { asChild: true, className: "w-full sm:w-auto" },
                        React.createElement(Button, { variant: "outline", size: "sm", className: "w-full sm:w-auto border-zinc-300 text-zinc-700 hover:bg-zinc-50 bg-transparent whitespace-nowrap" },
                            "Actions",
                            React.createElement(ChevronDown, { className: "ml-1 h-4 w-4" }))),
                    React.createElement(DropdownMenuContent, { align: "end", className: "min-w-[--radix-dropdown-menu-trigger-width]" },
                        showAddBaseCost && onAddBaseCost && (React.createElement(DropdownMenuItem, { onClick: onAddBaseCost }, "Add Cost")),
                        React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement(DropdownMenuItem, { onClick: (e) => {
                                            if (createInvoiceDisabled) {
                                                e.preventDefault();
                                                return;
                                            }
                                            setInvoiceModalOpen(true);
                                        }, className: createInvoiceDisabled
                                            ? 'opacity-50 cursor-not-allowed'
                                            : '', onSelect: createInvoiceDisabled
                                            ? (e) => e.preventDefault()
                                            : undefined }, "Create Invoice")),
                                createInvoiceDisabledReason && (React.createElement(TooltipContent, { side: "left" }, createInvoiceDisabledReason)))),
                        React.createElement(DropdownMenuItem, { onClick: () => setShowRerateDialog(true), disabled: !isRerateEnabled }, "Rerate Costs"))),
                React.createElement(InvoiceConfiguration, { shipperId: shipperId }))),
        React.createElement(ShipperRateCardLink, { orderId: orderId, shipperId: shipperId }),
        React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(Table, null,
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableHead, { className: "w-12" }, allChargesActioned || arFilter === 'actioned' ? (React.createElement(TooltipProvider, null,
                            React.createElement(Tooltip, null,
                                React.createElement(TooltipTrigger, { asChild: true },
                                    React.createElement("button", { type: "button", className: "inline-flex", disabled: true },
                                        React.createElement(Checkbox, { checked: false, disabled: true }))),
                                React.createElement(TooltipContent, null, "All charge lines are already invoiced.")))) : (React.createElement(Checkbox, { checked: allUnactionedSelected, onCheckedChange: handleSelectAll, disabled: unactionedCharges.length === 0 }))),
                        React.createElement(TableHead, { className: "text-left whitespace-nowrap" }, "Charge"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Cost"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Shipment"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Equipment"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Entered Date"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Rate Source"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Invoice"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Invoice Date"),
                        React.createElement(TableHead, { className: "whitespace-nowrap" }, "Invoice Status"),
                        showOptionalColumns && (React.createElement(React.Fragment, null,
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Email Sent"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "EDI Sent"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Reason Code"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Notes"),
                            React.createElement(TableHead, { className: "whitespace-nowrap" }, "Last User"))),
                        React.createElement(TableHead, { className: "w-12" },
                            React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0", onClick: () => setShowOptionalColumns(!showOptionalColumns), "aria-label": showOptionalColumns
                                    ? 'Hide optional columns'
                                    : 'Show optional columns', title: showOptionalColumns
                                    ? 'Hide optional columns'
                                    : 'Show optional columns' },
                                React.createElement(ChevronRight, { className: cn('h-4 w-4 transition-transform', showOptionalColumns && 'rotate-90') }))),
                        React.createElement(TableHead, null, "Actions"))),
                React.createElement(TableBody, null,
                    isLoadingAR && !isErrorAR
                        ? Array.from({ length: 5 }).map((_, i) => (React.createElement(TableRow, { key: `ar-skel-${i}` },
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-4 rounded" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-32" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-16" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-24" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-20" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-20" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-28" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-24" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-20" })),
                            showOptionalColumns && (React.createElement(React.Fragment, null,
                                React.createElement(TableCell, null,
                                    React.createElement(Skeleton, { className: "h-4 w-12" })),
                                React.createElement(TableCell, null,
                                    React.createElement(Skeleton, { className: "h-4 w-12" })),
                                React.createElement(TableCell, null,
                                    React.createElement(Skeleton, { className: "h-8 w-8 rounded-full" })),
                                React.createElement(TableCell, null,
                                    React.createElement(Skeleton, { className: "h-4 w-20" })),
                                React.createElement(TableCell, null,
                                    React.createElement(Skeleton, { className: "h-4 w-28" })),
                                React.createElement(TableCell, null,
                                    React.createElement(Skeleton, { className: "h-4 w-20" })))),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-4 w-4" })),
                            React.createElement(TableCell, null,
                                React.createElement(Skeleton, { className: "h-6 w-16" })))))
                        : filteredCharges.map((charge) => {
                            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
                            const isUnactioned = !((_a = charge.invoice_id) === null || _a === void 0 ? void 0 : _a.number);
                            return (React.createElement(TableRow, { key: charge._id },
                                React.createElement(TableCell, null, allChargesActioned || arFilter === 'actioned' ? (React.createElement(TooltipProvider, null,
                                    React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("button", { type: "button", className: "inline-flex", disabled: true },
                                                React.createElement(Checkbox, { checked: false, disabled: true }))),
                                        React.createElement(TooltipContent, null, "All charge lines are already invoiced.")))) : isUnactioned ? (React.createElement(Checkbox, { checked: selectedCharges.has(charge._id), onCheckedChange: (checked) => handleSelectCharge(charge._id, checked) })) : (React.createElement(TooltipProvider, null,
                                    React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("button", { type: "button", className: "inline-flex", disabled: true },
                                                React.createElement(Checkbox, { checked: false, disabled: true }))),
                                        React.createElement(TooltipContent, null, "This charge line is already invoiced."))))),
                                React.createElement(TableCell, { className: "whitespace-nowrap" },
                                    React.createElement("div", { className: "flex items-center gap-2" },
                                        React.createElement(Tooltip, null,
                                            React.createElement(TooltipTrigger, { asChild: true },
                                                React.createElement("button", { className: "flex items-center gap-1 hover:text-muted-foreground" },
                                                    React.createElement("span", null, t(sluggify(charge.message), charge.message)),
                                                    React.createElement(Info, { className: "h-4 w-4 text-muted-foreground" }))),
                                            React.createElement(TooltipContent, { side: "right", className: "bg-black text-white p-3" },
                                                React.createElement("div", { className: "space-y-1" },
                                                    React.createElement("div", { className: "font-semibold" }, "Charge Code"),
                                                    React.createElement("div", { className: "text-sm" }, charge.charge_code || 'N/A'),
                                                    React.createElement("div", { className: "font-semibold mt-2" }, "Charge ID"),
                                                    React.createElement("div", { className: "text-sm" }, charge.charge_id || 'N/A')))),
                                        !((_b = charge.shipment) === null || _b === void 0 ? void 0 : _b.shipment_id) && (React.createElement(Badge, { className: "bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200" }, "Order-Level")),
                                        !((_c = charge.equipment) === null || _c === void 0 ? void 0 : _c.equipment_id) &&
                                            !((_e = (_d = charge.invoice_id) === null || _d === void 0 ? void 0 : _d.number) === null || _e === void 0 ? void 0 : _e.startsWith('CM-')) && (React.createElement(MissingEquipmentWarning, null)))),
                                React.createElement(TableCell, { className: "font-medium whitespace-nowrap" }, formatMoney(charge.value)),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_f = charge.shipment) === null || _f === void 0 ? void 0 : _f.shipment_id) ? (React.createElement(ShipmentExternalLink, { id: (_g = charge.shipment) === null || _g === void 0 ? void 0 : _g._id, label: (_h = charge.shipment) === null || _h === void 0 ? void 0 : _h.shipment_id })) : (NO_DATA)),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, (_k = (_j = charge.equipment) === null || _j === void 0 ? void 0 : _j.equipment_id) !== null && _k !== void 0 ? _k : NO_DATA),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, charge.created_at ? (React.createElement("span", { className: "text-sm" }, formatDate(charge.created_at, {
                                    dateFormat: 'll',
                                    timeFormat: 'LT',
                                }))) : (NO_DATA)),
                                React.createElement(TableCell, { className: "capitalize whitespace-nowrap" }, charge.rate_source || NO_DATA),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_l = charge.invoice_id) === null || _l === void 0 ? void 0 : _l._id) ? (React.createElement(Link, { to: `/invoices/${(_m = charge.invoice_id) === null || _m === void 0 ? void 0 : _m._id}`, className: "text-blue-600 hover:underline inline-flex items-center gap-1", target: "_blank", rel: "noopener noreferrer" },
                                    typeof ((_o = charge.invoice_id) === null || _o === void 0 ? void 0 : _o.number) === 'string' &&
                                        ((_p = charge.invoice_id) === null || _p === void 0 ? void 0 : _p.number.length) > 0
                                        ? (_q = charge.invoice_id) === null || _q === void 0 ? void 0 : _q.number
                                        : NO_DATA,
                                    React.createElement(ExternalLink, { className: "h-3 w-3" }))) : typeof ((_r = charge.invoice_id) === null || _r === void 0 ? void 0 : _r.number) === 'string' &&
                                    ((_s = charge.invoice_id) === null || _s === void 0 ? void 0 : _s.number.length) > 0 ? (React.createElement("span", null, (_t = charge.invoice_id) === null || _t === void 0 ? void 0 : _t.number)) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, ((_u = charge.invoice_id) === null || _u === void 0 ? void 0 : _u.timestamp) ? (React.createElement("span", { className: "text-sm" }, formatDate((_v = charge.invoice_id) === null || _v === void 0 ? void 0 : _v.timestamp, {
                                    dateFormat: 'll',
                                    timeFormat: 'LT',
                                }))) : (NO_DATA)),
                                React.createElement(TableCell, { className: "whitespace-nowrap" }, isValidStatus((_w = charge.invoice_id) === null || _w === void 0 ? void 0 : _w.status) ? (React.createElement(InvoiceStatusBadge, { status: charge.invoice_id.status })) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                                showOptionalColumns && (React.createElement(React.Fragment, null,
                                    React.createElement(TableCell, null, ((_x = charge.invoice_id) === null || _x === void 0 ? void 0 : _x.email_sent) ? (React.createElement(Tooltip, null,
                                        React.createElement(TooltipTrigger, { asChild: true },
                                            React.createElement("div", { className: "flex items-center" },
                                                React.createElement(CheckCircle, { className: "h-5 w-5 text-green-600" }))),
                                        React.createElement(TooltipContent, { side: "top", className: "bg-black text-white px-3 py-2" }, "Email Sent!"))) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                                    React.createElement(TableCell, null, ((_y = charge.invoice_id) === null || _y === void 0 ? void 0 : _y.edi_sent) ? (React.createElement(CheckCircle, { className: "h-5 w-5 text-green-600" })) : (React.createElement("span", { className: "text-muted-foreground" }, NO_DATA))),
                                    React.createElement(TableCell, null, (_z = charge.reason_code) !== null && _z !== void 0 ? _z : NO_DATA),
                                    React.createElement(TableCell, null, (_0 = charge.notes) !== null && _0 !== void 0 ? _0 : NO_DATA),
                                    React.createElement(TableCell, { className: "text-sm text-muted-foreground" }, (_1 = charge.user) !== null && _1 !== void 0 ? _1 : NO_DATA))),
                                React.createElement(TableCell, null),
                                React.createElement(TableCell, null, (((_2 = charge.invoice_id) === null || _2 === void 0 ? void 0 : _2._id) ||
                                    (onEditCharge &&
                                        (canShowEditAction(charge, isFinance) ||
                                            isOrderLevelBaseCostOrFsc(charge))) ||
                                    canShowDeleteAction(charge, isFinance)) && (React.createElement(DropdownMenu, null,
                                    React.createElement(DropdownMenuTrigger, { asChild: true },
                                        React.createElement(Button, { variant: "ghost", size: "sm", className: "h-8 w-8 p-0" },
                                            React.createElement(MoreHorizontal, { className: "h-4 w-4" }))),
                                    React.createElement(DropdownMenuContent, { align: "end" },
                                        ((_3 = charge.invoice_id) === null || _3 === void 0 ? void 0 : _3._id) && (React.createElement(DropdownMenuItem, { onClick: () => handleViewInvoice(charge) },
                                            React.createElement(ExternalLink, { className: "h-4 w-4 mr-2" }),
                                            "View Invoice")),
                                        onEditCharge &&
                                            (canShowEditAction(charge, isFinance) ||
                                                isOrderLevelBaseCostOrFsc(charge)) && (React.createElement(DropdownMenuItem, { onClick: () => onEditCharge(charge) },
                                            React.createElement(Edit, { className: "h-4 w-4 mr-2" }),
                                            "Edit")),
                                        canShowDeleteAction(charge, isFinance) && (React.createElement(DropdownMenuItem, { onClick: () => handleInitiateVoid(charge), className: "text-red-600" },
                                            React.createElement(X, { className: "h-4 w-4 mr-2" }),
                                            "Delete"))))))));
                        }),
                    isErrorAR && (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: showOptionalColumns ? 17 : 12 },
                            React.createElement("div", { className: "flex items-center gap-2 text-destructive" },
                                React.createElement(AlertCircle, { className: "h-4 w-4" }),
                                React.createElement("span", null, "Failed to load line items"))))),
                    !isLoadingAR && !isErrorAR && filteredCharges.length === 0 && (React.createElement(TableRow, null,
                        React.createElement(TableCell, { colSpan: showOptionalColumns ? 17 : 12, className: "text-muted-foreground" }, arLines.length === 0
                            ? 'No charges found'
                            : 'No charges match the selected filter'))),
                    !isLoadingAR && !isErrorAR && (React.createElement(TableRow, { className: "bg-muted/50 font-semibold" },
                        React.createElement(TableCell, null),
                        React.createElement(TableCell, null, "Total"),
                        React.createElement(TableCell, null, formatMoney(filteredTotal)),
                        React.createElement(TableCell, { colSpan: showOptionalColumns ? 14 : 9 }))),
                    isLoadingAR && !isErrorAR && (React.createElement(TableRow, { className: "bg-muted/50 font-semibold" },
                        React.createElement(TableCell, null),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-16" })),
                        React.createElement(TableCell, null,
                            React.createElement(Skeleton, { className: "h-4 w-24" })),
                        React.createElement(TableCell, { colSpan: showOptionalColumns ? 14 : 9 })))))),
        showInvoiceDetailsModal && (React.createElement(CargomaticModal, { title: "", show: showInvoiceDetailsModal, footer: null, body: React.createElement(InvoiceDetails, { invoiceId: (_a = selectedInvoiceCharge === null || selectedInvoiceCharge === void 0 ? void 0 : selectedInvoiceCharge.invoice_id) === null || _a === void 0 ? void 0 : _a._id }), onHide: () => {
                setShowInvoiceDetailsModal(false);
                setSelectedInvoiceCharge(null);
            }, dialogClassName: "modal-override" })),
        React.createElement(ConfirmModal, { dataTestId: "confirm-void-modal", show: showConfirmVoidModal, onHide: () => {
                setShowConfirmVoidModal(false);
                setChargeToVoid(null);
            }, onConfirm: handleConfirmVoid, title: "Confirm Delete", message: "Are you sure you want to delete this charge? This action cannot be undone.", affirmative: voidPending ? 'Deleting...' : 'Delete', primaryDisabled: voidPending, primaryButtonColor: "red" }),
        React.createElement(GenerateInvoiceDialog, { open: invoiceModalOpen, onClose: () => setInvoiceModalOpen(false), onSuccess: handleInvoiceSuccess, orderId: orderId, shipperId: shipperId, charges: unactionedCharges, initialSelectedIds: selectedCharges, groupLikeInvoiceItems: groupLikeInvoiceItems }),
        React.createElement(RerateCostsDialog, { open: showRerateDialog, onOpenChange: setShowRerateDialog, charges: rerateEligibleCharges, onRerate: handleRerate, isPending: rerateMutation.isPending })));
}
export default ARChargesTable;
